import { Button, Checkbox, ColorField, Dialog, Input, LabeledContainer } from '@schibsted-svp/react-ui';
import { PosterManager } from 'components/AssetForm/containers/PosterManager';
import Tooltip from 'components/ui/Tooltip';
import useToggle from 'hooks/useToggle';
import React from 'react';
import { Controller } from 'react-hook-form';
import { MdInfo } from 'react-icons/md';
import classnames from 'classnames/bind';
import css from './CustomizeThemeDialog.module.scss';
import { useThemeForm, INITIAL_THEME_FORM } from './hooks';
import type { Playlist, ThemeImageType } from './types';

const cln = classnames.bind(css);

type ThemeControl = ReturnType<typeof useThemeForm>['control'];

function ColorControl({
    control,
    name,
    label,
}: {
    control: ThemeControl;
    name:
        'background.lightModeColor' | 'background.darkModeColor' | 'videoCards.textColor' | 'videoCards.highlightColor';
    label: string;
}) {
    return (
        <Controller
            control={control}
            name={name}
            render={({ field, fieldState }) => (
                <div className={css.colorControl}>
                    <ColorField
                        label={label}
                        value={field.value}
                        error={fieldState.error?.message}
                        onChange={field.onChange}
                    />
                </div>
            )}
        />
    );
}

export function CustomizeThemeDialog({ playlist }: { playlist?: Playlist }) {
    const [isOpen, toggleOpen] = useToggle();

    //                 [ width, height ]
    const iconsMaximumSize: [number, number] = [240, 36];
    const iconsMinimumSize: [number, number] = [36, 36];
    const backgroundMaximumSize: [number, number] = [10000, 10000];
    const backgroundMinimumSize: [number, number] = [300, 250];

    // form state — no Redux involved, safe to initialize directly until hook is wired up
    const formApi = useThemeForm(INITIAL_THEME_FORM);
    const { control, register, formState } = formApi;
    const { isSubmitting, errors, defaultValues } = formState;

    // TODO: will come from useCustomizeThemeDialog hook — whether the icon is hidden (disables icon upload)
    const hideIcon = false;

    // TODO: will come from useCustomizeThemeDialog hook — dispatches uploadVideoPoster Redux action
    const onVideoPosterUpload = (() => {}) as (file: File, imageType: ThemeImageType) => void;

    // TODO: will come from useCustomizeThemeDialog hook — reverts uploaded images and closes the dialog
    const handleCancel = () => toggleOpen();

    // TODO: will come from useCustomizeThemeDialog hook — validates form and calls saveTheme API
    const handleSubmit = ((event: React.FormEvent) => {
        event.stopPropagation();
        toggleOpen();
    }) as unknown as (event: React.FormEvent) => void;

    const triggerButton = (
        <Button type="button" variant="standard" size="small" onClick={toggleOpen} disabled={!playlist}>
            Customize Carousel Theme
        </Button>
    );

    const dialogTrigger = (
        <div className={css.triggerRow}>
            {triggerButton}
            {defaultValues?.themeTitle && (
                <span className={css.currentTheme}>
                    Current theme:{' '}
                    <span className={css.currentThemeTitle} title={defaultValues.themeTitle}>
                        {defaultValues.themeTitle}
                    </span>
                </span>
            )}
        </div>
    );

    if (!playlist)
        return (
            <div className={css.triggerRow}>
                <Tooltip
                    theme="light"
                    content="Save the playlist first to customize the theme"
                    placement="top"
                    offset={[0, 10]}
                >
                    <div>{triggerButton}</div>
                </Tooltip>
            </div>
        );

    return (
        <Dialog
            isOpen={isOpen}
            heading="Customize Carousel Theme"
            onClose={handleCancel}
            dialogTrigger={dialogTrigger}
            className={css.dialog}
        >
            <form onSubmit={handleSubmit}>
                <Dialog.Section className={css.dialogSection}>
                    <div className={css.content}>
                        <div className={css.themeTitleRow}>
                            <LabeledContainer label="THEME TITLE">
                                <Input size="small" error={errors.themeTitle?.message} {...register('themeTitle')} />
                            </LabeledContainer>
                        </div>

                        <div className={css.sectionHeading}>Icons</div>

                        <div className={cln(css.twoColumnRow, { disabled: hideIcon })}>
                            {(['iconLight', 'iconDark'] as const).map((type) => (
                                <div key={type}>
                                    <PosterManager
                                        id={playlist.id}
                                        provider={playlist.provider}
                                        type={type}
                                        label={`${type === 'iconLight' ? 'light' : 'dark'} mode icon`}
                                        control={control}
                                        minimumSize={iconsMinimumSize}
                                        maximumSize={iconsMaximumSize}
                                        ready={!hideIcon}
                                        alwaysAllowDelete
                                        posterContainerClassName={css.posterContainerIcon}
                                        onVideoPosterUpload={onVideoPosterUpload}
                                    />
                                </div>
                            ))}
                        </div>

                        <div className={css.hideIconRow}>
                            <Checkbox label="Hide icon" {...register('hideIcon')} />
                            <Tooltip
                                theme="light"
                                content="Hides the header icon in the carousel. When enabled, icon upload is disabled."
                                placement="top"
                                offset={[0, 10]}
                            >
                                <span className={css.infoIcon}>
                                    <MdInfo />
                                </span>
                            </Tooltip>
                        </div>

                        <div className={css.sectionHeading}>Background</div>

                        <div className={css.twoColumnRow}>
                            <ColorControl control={control} name="background.lightModeColor" label="LIGHT MODE COLOR" />
                            <ColorControl control={control} name="background.darkModeColor" label="DARK MODE COLOR" />
                        </div>

                        <div className={css.twoColumnRow}>
                            {(['backgroundImageLight', 'backgroundImageDark'] as const).map((type) => (
                                <div key={type}>
                                    <PosterManager
                                        id={playlist.id}
                                        provider={playlist.provider}
                                        type={type}
                                        label={`${type === 'backgroundImageLight' ? 'light' : 'dark'} mode image`}
                                        control={control}
                                        ready
                                        enableCrop
                                        alwaysAllowDelete
                                        posterContainerClassName={css.posterContainerVertical}
                                        onVideoPosterUpload={onVideoPosterUpload}
                                    />
                                </div>
                            ))}
                        </div>

                        <div className={css.sectionHeading}>Video Cards</div>

                        <div className={css.twoColumnRow}>
                            <ColorControl control={control} name="videoCards.textColor" label="TEXT COLOR" />
                            <ColorControl control={control} name="videoCards.highlightColor" label="HIGHLIGHT COLOR" />
                        </div>
                    </div>
                    <div className={css.separator} />
                    <div className={css.iconsRequirements}>
                        <div className={css.resolutionText}>
                            <strong className={css.resolutionTextHeading}>Image requirements</strong>
                            <span>Height: {iconsMinimumSize[1]}px</span>
                            <span>
                                Width: {iconsMinimumSize[0]}-{iconsMaximumSize[0]}px
                            </span>
                        </div>
                    </div>
                    <div className={css.backgroundRequirements}>
                        <div className={css.resolutionText}>
                            <strong className={css.resolutionTextHeading}>Image requirements</strong>
                            <span>
                                Height: {backgroundMinimumSize[1]}-{backgroundMaximumSize[1]}px
                            </span>
                            <span>
                                Width: {backgroundMinimumSize[0]}-{backgroundMaximumSize[0]}px
                            </span>
                        </div>
                    </div>
                </Dialog.Section>

                <Dialog.Section mode="flexRight" variant="darker">
                    <Button type="button" variant="standard" onClick={handleCancel} disabled={isSubmitting}>
                        Cancel
                    </Button>
                    <Button type="button" variant="standard" onClick={() => {}}>
                        Preview
                    </Button>
                    <Button type="submit" variant="primary" loading={isSubmitting} disabled={isSubmitting}>
                        Save
                    </Button>
                </Dialog.Section>
            </form>
        </Dialog>
    );
}
